/**
 * The Bexio invoices that actually went into the reconciliation.
 *
 * `counted_documents` IS the authoritative counted set — the service nests each
 * counted invoice under the one tenancy it is mapped to — so this view flattens
 * that structure and never re-selects or re-aggregates documents itself. An invoice
 * the backend excluded (draft / canceled / unknown status, or no complete mapping)
 * cannot appear here, which is the point: this table answers "what was counted",
 * and the warnings section answers "what was not, and why".
 *
 * NOT shown, because the response does not carry them for a counted document:
 * an invoice date (`ReconCountedDocument` has `due_date` only) and any "days
 * overdue" figure (that would need a second clock beside the service's `today`).
 */
import React from "react";

import { INVOICE_STATUS_LABEL, fmtDateCH, fmtMoney, mapLabel } from "../bexioFormat";

const DASH = "—";

/** Status tone, matching the invoice workspace so one status looks the same product-wide. */
const STATUS_TONE = {
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
  partial:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  pending:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
};

function StatusPill({ status }) {
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        STATUS_TONE[status] || "border-border bg-muted text-muted-foreground"
      }`}
    >
      {mapLabel(INVOICE_STATUS_LABEL, status)}
    </span>
  );
}

export default function BexioCountedDocumentsTable({ rows, currency, onSelect }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th
              scope="col"
              className="w-[12%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground"
            >
              Beleg-Nr.
            </th>
            <th
              scope="col"
              className="w-[22%] px-4 py-2.5 text-xs font-medium text-muted-foreground"
            >
              Mietverhältnis / Objekt
            </th>
            <th
              scope="col"
              className="w-[16%] px-4 py-2.5 text-xs font-medium text-muted-foreground"
            >
              Mieter
            </th>
            <th
              scope="col"
              className="w-[12%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground"
            >
              Betrag
            </th>
            <th
              scope="col"
              className="w-[12%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground"
            >
              Bezahlt
            </th>
            <th
              scope="col"
              className="w-[12%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground"
            >
              Offener Betrag
            </th>
            <th
              scope="col"
              className="w-[10%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground"
            >
              Fällig am
            </th>
            <th
              scope="col"
              className="w-[12%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground"
            >
              Bexio Status
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr
              key={d.key}
              className="border-b border-border last:border-0 hover:bg-muted/50"
            >
              <td className="whitespace-nowrap px-4 py-2.5">
                <button
                  type="button"
                  onClick={() => onSelect(d)}
                  aria-label={`Details zu ${
                    d.document_nr || `Bexio-Rechnung ${d.bexio_invoice_id}`
                  } anzeigen`}
                  className="rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {/* No document number is a real state in the mirror; the Bexio id
                      identifies the row instead of an invented placeholder. */}
                  {d.document_nr || `#${d.bexio_invoice_id}`}
                </button>
              </td>
              <td className="max-w-0 px-4 py-2.5">
                <span className="block truncate text-foreground">
                  {d.tenancy_reference_number || DASH}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {[d.unit_label, d.room_label].filter(Boolean).join(" · ") || DASH}
                </span>
              </td>
              <td className="max-w-0 truncate px-4 py-2.5 text-muted-foreground">
                {d.tenant_display_name || DASH}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-foreground">
                {fmtMoney(d.total, currency)}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                {fmtMoney(d.total_paid, currency)}
              </td>
              <td
                className={`whitespace-nowrap px-4 py-2.5 text-right tabular-nums ${
                  Number(d.total_remaining || 0) !== 0
                    ? "font-medium text-foreground"
                    : "text-muted-foreground"
                }`}
              >
                {fmtMoney(d.total_remaining, currency)}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">
                {/* A counted invoice may legitimately have no due date — the service
                    puts exactly those into "nicht fällig / ohne Fälligkeit". */}
                {d.due_date ? fmtDateCH(d.due_date) : DASH}
              </td>
              <td className="px-4 py-2.5">
                <StatusPill status={d.bexio_status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
